// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Overridden Fields Summary
 *
 * Shows a collapsible list of fields that override inherited values.
 * Each field has a "clear" button to reset to the inherited value.
 * Node authors don't need to integrate this - it's shown automatically
 * in the ConfigurationPanel for nodes that extend a base.
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, RotateCcw, Link2, Save } from 'lucide-react'
import { Button } from '@/components/ui'
import type { InheritanceMetadata } from '../types/inheritance'

interface OverriddenFieldsSummaryProps {
  inheritance: InheritanceMetadata
  onClearField: (fieldPath: string) => void
  onSave?: () => void
  isDirty?: boolean
  disabled?: boolean
}

/**
 * Format field path for display.
 * "settings.temperature" → "Temperature"
 * "settings.model_variant" → "Model Variant"
 */
function formatFieldPath(path: string): string {
  // Take last segment after the last dot
  const lastSegment = path.split('.').pop() ?? path
  // Convert snake_case to Title Case
  return lastSegment
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Get the parent path for grouping.
 * "settings.temperature" → "settings"
 * "label" → ""
 */
function getParentPath(path: string): string {
  const parts = path.split('.')
  return parts.length > 1 ? parts.slice(0, -1).join('.') : ''
}

/**
 * Group fields by their parent path for cleaner display.
 */
function groupFieldsByParent(fields: string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>()
  for (const field of fields) {
    const parent = getParentPath(field)
    const existing = groups.get(parent) ?? []
    existing.push(field)
    groups.set(parent, existing)
  }
  return groups
}

export function OverriddenFieldsSummary({
  inheritance,
  onClearField,
  onSave,
  isDirty = false,
  disabled = false,
}: OverriddenFieldsSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Filter to only settings fields (most relevant for users)
  const overriddenFields = inheritance.overriddenFields.filter(
    (f) => f.startsWith('settings.') || f === 'label'
  )

  if (overriddenFields.length === 0) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <Link2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium text-blue-900">
              Fully inherited from base
            </p>
            <p className="text-xs text-blue-700">
              All settings are inherited from{' '}
              <code className="bg-blue-100 px-1 rounded">
                {inheritance.sourceInstanceId}
              </code>
            </p>
          </div>
          {isDirty && onSave && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
              onClick={onSave}
              disabled={disabled}
            >
              <Save className="h-3 w-3 mr-1" />
              Save to update
            </Button>
          )}
        </div>
      </div>
    )
  }

  const groupedFields = groupFieldsByParent(overriddenFields)

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="flex items-center gap-2 flex-1 text-left"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-amber-600" />
          ) : (
            <ChevronRight className="h-4 w-4 text-amber-600" />
          )}
          <span className="text-sm font-medium text-amber-900">
            {overriddenFields.length} field{overriddenFields.length !== 1 ? 's' : ''} overridden
          </span>
          <span className="text-xs text-amber-700 ml-auto mr-2">
            Base: {inheritance.sourceInstanceId}
          </span>
        </button>
        {isDirty && onSave && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={onSave}
            disabled={disabled}
          >
            <Save className="h-3 w-3 mr-1" />
            Save to update
          </Button>
        )}
      </div>

      {isExpanded && (
        <div className="mt-3 space-y-2 pl-6">
          {Array.from(groupedFields.entries()).map(([parent, fields]) => (
            <div key={parent || 'root'} className="space-y-1">
              {parent && (
                <div className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                  {formatFieldPath(parent)}
                </div>
              )}
              {fields.map((fieldPath) => (
                <div
                  key={fieldPath}
                  className="flex items-center justify-between py-1 px-2 rounded bg-amber-100/50"
                >
                  <span className="text-sm text-amber-900">
                    {formatFieldPath(fieldPath)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-200"
                    onClick={() => onClearField(fieldPath)}
                    disabled={disabled}
                    aria-label={`Reset ${formatFieldPath(fieldPath)} to inherited value`}
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Reset
                  </Button>
                </div>
              ))}
            </div>
          ))}
          <p className="text-xs text-amber-600 pt-2">
            Click Reset to restore the inherited value from the base node.
          </p>
        </div>
      )}
    </div>
  )
}
